/**
 * Duvar level scene. The level map (Phaser, @kit/map) starts it with `{ level }`; the level itself
 * renders with three.js (view3d.ts) under a DOM HUD, while Phaser's loop sleeps. Leaving wakes
 * Phaser and hands the result back to the map. Rules: core/game.ts.
 */
import Phaser from "phaser";
import { decodePixels, type Picture } from "@enjoyment/core";
import { loadHdPicture, loadPictures } from "@shared/art/pictures";
import { sfx, unlockAudio } from "@shared/audio";
import { exposeBotHook, loadSave, writeSave } from "@kit/boot";
import { openGuess } from "@kit/guess";
import { currentLang, publicGameUrl } from "@kit/lang";
import { afterSkip, afterWin, mapText, openMap, starLine, type MapReturn } from "@kit/map";
import { recordSkip, recordWin, type WinOutcome } from "@kit/progress";
import { track } from "@kit/telemetry";
import { share } from "@kit/ui";
import { columnOpen, createGame, freeBlocks, giveUp, guessAnswer, resultTaps, revealedFraction, shareGrid, starsFor, tapBlock, type DuvarLevel, type DuvarState } from "./core/game";
import { generateLevel, pickPicture, themePool } from "./core/generator";
import { revealPlan } from "./core/bot";
import { t } from "./i18n";
import { DuvarView } from "./view3d";

const ID = "duvar";
const MAX_FAILS = 3; // H4: three losses unlock a free skip

type Save = { level: number; fails: Record<string, number>; best: Record<string, number> };

function pixelCanvas(p: Picture): HTMLCanvasElement {
  const cv = document.createElement("canvas");
  cv.width = cv.height = p.size;
  const ctx = cv.getContext("2d")!;
  decodePixels(p).forEach((c, i) => {
    if (c < 0) return;
    ctx.fillStyle = p.palette[c]!;
    ctx.fillRect(i % p.size, Math.floor(i / p.size), 1, 1);
  });
  return cv;
}

export class DuvarScene extends Phaser.Scene {
  private levelArg?: number;
  private save!: Save;
  private level!: DuvarLevel;
  private state?: DuvarState;
  private picture?: Picture;
  private pool: Picture[] = [];
  private hd?: HTMLImageElement;
  private view?: DuvarView;
  private root?: HTMLDivElement;
  private closeGuess?: () => void;
  private outcome?: WinOutcome;
  private startedAt = 0;
  private auto = false;
  private dead = false;
  private fpsTimer = 0;

  constructor() {
    super(ID);
  }

  init(data?: { level?: number }): void {
    this.levelArg = data?.level;
    this.state = undefined;
    this.picture = undefined;
    this.hd = undefined;
    this.view = undefined;
    this.root = undefined;
    this.closeGuess = undefined;
    this.outcome = undefined;
    this.auto = false;
    this.dead = false;
  }

  create(): void {
    this.events.once("shutdown", () => this.teardown());
    this.save = loadSave<Save>(ID, { level: 1, fails: {}, best: {} });
    this.save.fails ??= {};
    this.save.best ??= {};
    this.level = generateLevel(Math.max(1, Math.floor(this.levelArg ?? this.save.level)));
    this.buildDom();
    this.game.canvas.style.visibility = "hidden";
    this.game.loop.sleep();
    loadPictures()
      .then((pack) => {
        if (this.dead) return;
        this.pool = themePool(this.level, pack.pictures);
        this.picture = pickPicture(this.level, pack.pictures);
        this.begin();
      })
      .catch(() => this.message(t("loading")));
  }

  // ---------------------------------------------------------------- setup

  private buildDom(): void {
    const root = document.createElement("div");
    root.className = "duvar";
    root.innerHTML = `
      <header class="duvar-top">
        <div class="duvar-title"></div>
        <button type="button" class="duvar-back"></button>
        <div class="duvar-score"><small></small><b>0</b></div>
      </header>
      <div class="duvar-status"></div>
      <div class="duvar-stage" role="img"></div>
      <footer class="duvar-bottom">
        <div class="duvar-actions">
          <button type="button" class="duvar-give"></button>
          <button type="button" class="duvar-guess"></button>
        </div>
        <p class="duvar-hint"></p>
      </footer>
      <div class="duvar-card" hidden></div>
      <div class="duvar-fps" hidden></div>`;
    const $ = <T extends HTMLElement>(s: string) => root.querySelector<T>(s)!;
    $(".duvar-title").textContent = `${t("title")} · ${this.level.n}`;
    $(".duvar-back").textContent = mapText("map");
    $(".duvar-score small").textContent = t("taps");
    $(".duvar-guess").textContent = t("guess");
    $(".duvar-give").textContent = t("giveUp");
    $(".duvar-hint").textContent = t("hint");
    $(".duvar-stage").setAttribute("aria-label", t("question"));
    $(".duvar-status").textContent = t("loading");
    $(".duvar-back").addEventListener("click", () => this.leave());
    $(".duvar-guess").addEventListener("click", () => this.openGuessCard());
    $(".duvar-give").addEventListener("click", () => this.lose());
    document.body.append(root);
    this.root = root;
  }

  private begin(): void {
    if (!this.root || !this.picture) return;
    this.state = createGame(this.level, this.picture);
    const stage = this.root.querySelector<HTMLElement>(".duvar-stage")!;
    try {
      this.view = new DuvarView(stage, this.level, (id) => this.onTap(id));
    } catch {
      this.message(t("noWebgl"));
      return;
    }
    this.view.setPicture(pixelCanvas(this.picture));
    const id = this.picture.id;
    void loadHdPicture(id).then((img) => {
      if (!img || this.dead || this.picture?.id !== id) return;
      this.hd = img;
      this.view?.setPicture(img);
    });
    window.addEventListener("resize", this.onResize);
    if (new URLSearchParams(location.search).has("fps")) {
      const fps = this.root.querySelector<HTMLElement>(".duvar-fps")!;
      fps.hidden = false;
      this.fpsTimer = window.setInterval(() => (fps.textContent = `${this.view?.fps ?? 0} fps`), 500);
    }
    this.startedAt = Date.now();
    const { n, w, h, d } = this.level;
    track("level_start", { level: n, w, h, d, picture: id, attempt: (this.save.fails[String(n)] ?? 0) + 1 });
    this.refresh();
    exposeBotHook({
      state: () => this.botState(),
      autoplay: () => this.autoplay(),
      share: () => this.shareResult(),
    });
  }

  private onResize = (): void => this.view?.resize();

  private teardown(): void {
    this.dead = true;
    this.closeGuess?.();
    window.clearInterval(this.fpsTimer);
    window.removeEventListener("resize", this.onResize);
    this.view?.dispose();
    this.view = undefined;
    this.root?.remove();
    this.root = undefined;
    this.game.canvas.style.visibility = "";
  }

  /** Back to Phaser: wake its loop, then let the map take over. */
  private leave(back?: MapReturn): void {
    this.game.loop.wake();
    openMap(this, ID, back);
  }

  private message(text: string): void {
    const el = this.root?.querySelector<HTMLElement>(".duvar-status");
    if (el) el.textContent = text;
  }

  // ---------------------------------------------------------------- play

  private refresh(): void {
    const s = this.state;
    if (!s || !this.root) return;
    this.root.querySelector<HTMLElement>(".duvar-score b")!.textContent = String(s.taps + s.wrong * 2);
    this.message(t("status", { pct: Math.round(revealedFraction(s) * 100), bumps: s.bumps }));
    const playing = s.status === "playing";
    this.root.querySelector<HTMLButtonElement>(".duvar-guess")!.disabled = !playing;
    this.root.querySelector<HTMLButtonElement>(".duvar-give")!.disabled = !playing;
  }

  private onTap(id: number, fromBot = false): void {
    const s = this.state;
    if (!s || s.status !== "playing" || this.closeGuess || (this.auto && !fromBot)) return;
    unlockAudio();
    const r = tapBlock(s, id);
    if (r.kind === "exit") {
      this.view?.exit(id);
      for (const c of r.opened) this.view?.openColumn(c.x, c.y);
      if (r.opened.length) sfx.goalTick();
      else sfx.pop(2);
    } else if (r.kind === "bump") {
      this.view?.bump(id, r.blocker);
      sfx.nope();
      navigator.vibrate?.(12);
    }
    track("move", { level: this.level.n, taps: s.taps, kind: r.kind, opened: r.opened.length, revealed: Math.round(revealedFraction(s) * 100) });
    this.refresh();
    if (s.left === 0 && !fromBot) this.openGuessCard();
  }

  /** What the player has seen: the picture where whole columns are gone, dark squares elsewhere. */
  private paintRevealed(ctx: CanvasRenderingContext2D, size: number): void {
    const s = this.state;
    if (!s || !this.picture) return;
    const { w, h } = this.level;
    const src: CanvasImageSource = this.hd ?? pixelCanvas(this.picture);
    const side = this.hd ? this.hd.naturalWidth || 384 : this.picture.size;
    // Same crop as the wall texture: the long side of the w×h wall spans the picture.
    const sw = w >= h ? side : (side * w) / h;
    const sh = h >= w ? side : (side * h) / w;
    ctx.imageSmoothingEnabled = !!this.hd;
    ctx.fillStyle = "#f4efe6";
    ctx.fillRect(0, 0, size, size);
    const dw = w >= h ? size : (size * w) / h;
    const dh = h >= w ? size : (size * h) / w;
    const ox = (size - dw) / 2;
    const oy = (size - dh) / 2;
    ctx.drawImage(src, (side - sw) / 2, (side - sh) / 2, sw, sh, ox, oy, dw, dh);
    const cw = dw / w;
    const ch = dh / h;
    for (let y = 0; y < h; y++)
      for (let x = 0; x < w; x++) {
        if (columnOpen(s, x, y)) continue;
        ctx.fillStyle = "#30365f";
        ctx.fillRect(ox + x * cw, oy + (h - 1 - y) * ch, cw + 0.5, ch + 0.5);
      }
  }

  private openGuessCard(): void {
    const s = this.state;
    if (!s || s.status !== "playing" || this.closeGuess) return;
    unlockAudio();
    this.root?.classList.add("is-guessing");
    this.closeGuess = openGuess(this, {
      question: t("question"),
      pool: this.pool,
      lang: currentLang(),
      paint: (ctx, size) => this.paintRevealed(ctx, size),
      tried: () => s.wrongLabels,
      onSubmit: (text) => {
        const out = guessAnswer(s, text);
        track("guess", { level: this.level.n, correct: out.correct, taps: s.taps, reveal: Math.round(revealedFraction(s) * 100), wrong: s.wrong });
        this.refresh();
        if (out.correct) {
          window.setTimeout(() => this.win(), 380);
          return { correct: true, message: t("correct"), finished: true };
        }
        if (!out.repeat) sfx.nope();
        return { correct: false, message: out.repeat ? t("repeat") : t("wrong"), finished: false };
      },
      onClose: () => {
        this.closeGuess = undefined;
        this.root?.classList.remove("is-guessing");
      },
    });
  }

  private remaining(): number[] {
    const s = this.state;
    return s ? s.level.blocks.filter((b) => s.present[b.id]).map((b) => b.id) : [];
  }

  private win(): void {
    const s = this.state;
    if (!s || s.status !== "won" || this.dead) return;
    const n = this.level.n;
    const key = String(n);
    const stars = starsFor(s);
    const taps = resultTaps(s);
    this.outcome = recordWin(ID, n, stars);
    delete this.save.fails[key];
    this.save.level = Math.max(this.save.level, n + 1);
    this.save.best[key] = Math.min(this.save.best[key] ?? Infinity, taps);
    writeSave(ID, this.save);
    track("level_end", { level: n, result: "won", taps, bumps: s.bumps, wrong: s.wrong, reveal: Math.round(s.revealedAtSolve * 100), stars, secs: Math.round((Date.now() - this.startedAt) / 1000) });
    if (s.revealedAtSolve <= 0.25) track("moment", { level: n, type: "early_guess", reveal: Math.round(s.revealedAtSolve * 100) });
    sfx.win();
    this.view?.clearAll(this.remaining());
    const outcome = this.outcome;
    this.card(t("won"), `${starLine(stars)}\n${t("wonSub", { taps, pct: Math.round(s.revealedAtSolve * 100) })}\n${this.picture?.label[currentLang()] ?? this.picture?.label.tr ?? ""}`, [
      { label: t("share"), kind: "ghost", onClick: () => void this.shareResult() },
      { label: mapText("continue"), kind: "primary", onClick: () => this.leave(afterWin(n, outcome)) },
    ]);
  }

  private lose(): void {
    const s = this.state;
    if (!s || s.status !== "playing") return;
    giveUp(s);
    const n = this.level.n;
    const key = String(n);
    const fails = (this.save.fails[key] ?? 0) + 1;
    this.save.fails[key] = fails;
    writeSave(ID, this.save);
    track("level_end", { level: n, result: "lost", taps: s.taps, bumps: s.bumps, fails, reveal: Math.round(revealedFraction(s) * 100), secs: Math.round((Date.now() - this.startedAt) / 1000) });
    sfx.lose();
    this.refresh();
    this.view?.clearAll(this.remaining());
    const name = this.picture?.label[currentLang()] ?? this.picture?.label.tr ?? "";
    this.card(t("lost", { name }), fails >= MAX_FAILS ? t("skipHelp") : t("lostSub"), [
      ...(fails >= MAX_FAILS ? [{ label: t("skip"), kind: "ghost" as const, onClick: () => this.skip() }] : []),
      { label: t("retry"), kind: "primary", onClick: () => this.retry() },
    ]);
  }

  private retry(): void {
    this.game.loop.wake();
    this.scene.restart({ level: this.level.n });
  }

  /** H4: a level that beat the player three times can be skipped, for free. */
  private skip(): void {
    const n = this.level.n;
    delete this.save.fails[String(n)];
    this.save.level = Math.max(this.save.level, n + 1);
    writeSave(ID, this.save);
    track("level_end", { level: n, result: "skip", taps: this.state?.taps ?? 0 });
    this.leave(afterSkip(n, recordSkip(ID, n)));
  }

  private card(title: string, sub: string, buttons: { label: string; kind: "primary" | "ghost"; onClick: () => void }[]): void {
    const el = this.root?.querySelector<HTMLElement>(".duvar-card");
    if (!el) return;
    el.innerHTML = `<div class="duvar-card-box"><h2></h2><p></p><div class="duvar-card-buttons"></div></div>`;
    el.querySelector("h2")!.textContent = title;
    const p = el.querySelector("p")!;
    p.textContent = sub;
    p.classList.toggle("stars", /^[★☆]/.test(sub)); // only the win card leads with a star line
    const row = el.querySelector<HTMLElement>(".duvar-card-buttons")!;
    for (const b of buttons) {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = `duvar-btn ${b.kind}`;
      btn.textContent = b.label;
      btn.addEventListener("click", b.onClick);
      row.append(btn);
    }
    // Let the last blocks fly out before the card covers the picture.
    window.setTimeout(() => {
      if (!this.dead) el.hidden = false;
    }, 650);
  }

  private async shareResult(): Promise<boolean> {
    const s = this.state;
    if (!s) return false;
    const n = this.level.n;
    track("share_click", { level: n, taps: resultTaps(s) });
    const body = `${t("shareText", { n, taps: s.status === "won" ? resultTaps(s) : s.taps })}\n${shareGrid(s)}`;
    const ok = await share(body, `${publicGameUrl(ID)}?level=${n}`, "Duvar — Enjoyment");
    if (ok) track("share_done", { level: n });
    return ok;
  }

  // ---------------------------------------------------------------- bot hook

  /** Smart-peek bot: open the middle of the picture, then name it. */
  private async autoplay(): Promise<void> {
    const s = this.state;
    if (!s || this.auto || s.status !== "playing") return;
    this.auto = true;
    for (const id of revealPlan(this.level, 0.35)) {
      if (this.dead || s.status !== "playing") break;
      this.onTap(id, true);
      await new Promise((r) => window.setTimeout(r, 60));
    }
    this.auto = false;
    if (!this.dead && s.status === "playing" && this.picture) {
      guessAnswer(s, this.picture.label.tr ?? "");
      this.refresh();
      this.win();
    }
  }

  private botState(): Record<string, unknown> {
    const s = this.state;
    const free = s ? freeBlocks(s).slice(0, 16) : [];
    const blocked = s ? s.level.blocks.find((b) => s.present[b.id] && b.z === s.level.d - 1 && !free.includes(b.id) && !freeBlocks(s).includes(b.id))?.id ?? -1 : -1;
    const rect = (sel: string) => {
      const r = this.root?.querySelector(sel)?.getBoundingClientRect();
      return r ? { x: r.left + r.width / 2, y: r.top + r.height / 2 } : null;
    };
    return {
      level: this.level.n,
      phase: s?.status ?? "loading",
      taps: s?.taps ?? 0,
      bumps: s?.bumps ?? 0,
      left: s?.left ?? 0,
      revealed: s ? revealedFraction(s) : 0,
      free,
      blocked,
      hd: !!this.hd,
      fps: this.view?.fps ?? 0,
      card: !(this.root?.querySelector<HTMLElement>(".duvar-card")?.hidden ?? true),
      page: {
        blocks: Object.fromEntries(free.map((id) => [id, this.view?.screenPoint(id)])),
        blocked: blocked >= 0 ? this.view?.screenPoint(blocked) : null,
        guess: rect(".duvar-guess"),
        back: rect(".duvar-back"),
        stage: rect(".duvar-stage"),
      },
    };
  }
}
